import { Box, Flex, Text, IconButton, HStack } from "@chakra-ui/react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Conversation } from "../types/Conversation.type";
import { Message } from "../types/Message.type";
import { MessageBubble } from "./MessageBubble";
import { MessageComposer } from "./MessageComposer";
import { fetchMessagesForConversation } from "../api";
import { FiCornerUpLeft } from "react-icons/fi";
import Tooltip from "components/Tooltip";
import { LoadingSpinner } from "components/Loading/LoadingSpinner";

interface ConversationViewProps {
	allowNewMessages?: boolean;
	conversation: Conversation;
	scrollOnLoad: boolean;
	showBackButton: boolean;
	goBackOnMobile: () => void;
	onSendingMessage?: (isSending: boolean) => void;
}

/**
 * Displays all the messages for the given conversation
 * @param param0
 * @returns
 */
export const ConversationView: React.FC<ConversationViewProps> = ({
	allowNewMessages = true,
	conversation,
	scrollOnLoad = false,
	showBackButton,
	goBackOnMobile,
	onSendingMessage,
}) => {
	const [messages, setMessages] = useState<Message[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const endOfMessagesRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		setIsLoading(true);
		fetchMessagesForConversation(conversation.id)
			.then((data) => setMessages(data))
			.finally(() => {
				setIsLoading(false);
			});
	}, [conversation.id]);

	const onNewMessage = (message: Message) => {
		setMessages([...messages, message]);
	};

	useLayoutEffect(() => {
		// Scroll to bottom after messages are loaded
		if (scrollOnLoad && endOfMessagesRef.current) {
			endOfMessagesRef.current.scrollIntoView({ behavior: "smooth" });
		}
	}, [messages, scrollOnLoad]);

	return (
		<Flex
			direction="column"
			h="full"
			minW={0}
			w="full"
		>
			<Box
				p={4}
				className="border-b"
			>
				<HStack>
					<Tooltip label="Go back to all conversations">
						<IconButton
							aria-label="go back to conversation view"
							display={{
								base: showBackButton ? "flex" : "none",
							}}
							icon={
								<FiCornerUpLeft
									size={30}
									onClick={goBackOnMobile}
								/>
							}
						/>
					</Tooltip>
					<Text fontWeight="bold">
						#{conversation.reference_id} {conversation.subject}
					</Text>
				</HStack>
			</Box>

			<Box
				flex="1"
				overflowY="auto"
				className="px-4 py-2"
			>
				{isLoading && (
					<>
						<LoadingSpinner
							fontSize="sm"
							horizontal
							spinnerSize="sm"
							verticalPadding={1}
						/>
					</>
				)}
				{messages &&
					messages.map((msg) => (
						<MessageBubble
							isLoading={isLoading}
							key={msg.id}
							message={msg}
						/>
					))}
				<div ref={endOfMessagesRef} />
			</Box>

			{allowNewMessages && (
				<div className="border-t">
					<MessageComposer
						conversationId={conversation.id}
						onNewMessage={onNewMessage}
						onSendingMessage={onSendingMessage}
					/>
				</div>
			)}
		</Flex>
	);
};
